import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import CommentItem from './CommentItem';
import MentionTextarea from './MentionTextarea';
import { commentsService } from '../../services/comments';
import { useAuth } from '../../hooks/useAuth';
import type { Comment } from '../../types/comment';

interface CommentsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  ascentLogId: string;
  scrollToCommentId?: string;
}

export default function CommentsDialog({ open, onOpenChange, ascentLogId, scrollToCommentId }: CommentsDialogProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(false);
  const [newBody, setNewBody] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    const loadData = async () => {
      setLoading(true);
      try {
        const commentsRes = await commentsService.getComments(ascentLogId);
        setComments(commentsRes.data);
      } catch {
        // errors shown by api-client
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [open, ascentLogId]);

  // Scroll to target comment after comments load
  useEffect(() => {
    if (!scrollToCommentId || loading || comments.length === 0) return;

    const el = document.getElementById(`comment-${scrollToCommentId}`);
    if (el && scrollContainerRef.current) {
      setTimeout(() => {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('bg-accent');
        setTimeout(() => el.classList.remove('bg-accent'), 1500);
      }, 100);
    }
  }, [scrollToCommentId, loading, comments]);

  const handleSubmit = async () => {
    if (!newBody.trim()) return;
    setSubmitting(true);
    try {
      const res = await commentsService.createComment(ascentLogId, newBody.trim());
      setComments(prev => [...prev, res.data]);
      setNewBody('');
    } finally {
      setSubmitting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      handleSubmit();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg w-full flex flex-col max-h-[80vh]">
        <DialogHeader>
          <DialogTitle>{t('comments.title')}</DialogTitle>
        </DialogHeader>

        <div>
          {loading ? (
            <div className="py-8 text-center text-muted-foreground text-sm">{t('common.loading')}</div>
          ) : comments.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground text-sm">{t('comments.noComments')}</div>
          ) : (
            <div ref={scrollContainerRef} className="max-h-[45vh] overflow-y-auto pr-1">
              <div className="space-y-4 py-1">
                {comments.map(comment => (
                  <CommentItem
                    key={comment.id}
                    id={`comment-${comment.id}`}
                    comment={comment}
                    currentUserId={user?.id?.toString()}
                    ascentLogId={ascentLogId}
                    onUpdated={(updated) =>
                      setComments(prev => prev.map(c => c.id === updated.id ? updated : c))
                    }
                    onDeleted={(id) =>
                      setComments(prev => prev.filter(c => c.id !== id))
                    }
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        {user && (
          <div className="border-t pt-3 space-y-2">
            <MentionTextarea
              value={newBody}
              onChange={setNewBody}
              disabled={submitting}
              minRows={2}
              onKeyDown={handleKeyDown}
            />
            <div className="flex justify-end">
              <Button
                size="sm"
                onClick={handleSubmit}
                disabled={submitting || !newBody.trim()}
              >
                {t('comments.send')}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
